import {
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';

import {
    EzString
} from '/ezlibrary/helpers/EzHelpers.js';

/**
 * @class
 * @description
 * Provides information for a time zone
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *      import { EzTimeZoneInfo } from '/ezlibrary/enums/EzTimeZone.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzTimeZoneInfo {
    /**
     * Creates a new instance of EzTimeZoneInfo
     * @param {string} countryId
     * @param {string} timeZoneId
     * @param {string} stdUTCOffset
     * @param {undefined|null|string} dstUTCOffset
     * Default: stdUTCOffset
     * @param {undefined|null|string} abbreviation
     * Default: timeZoneId
     * @param {undefined|null|string} description
     * Default: timeZoneId
     */
    constructor(countryId, timeZoneId, stdUTCOffset, dstUTCOffset, abbreviation, description, primaryCityTimeZone) {
        if (!EzString.hasText(timeZoneId)) {
            throw new EzBadParamException(
                'timeZoneId',
                this,
                'constructor(countryId, timeZoneId, stdUTCOffset, dstUTCOffset, abbreviation, description, primaryCityTimeZone)');
        }

        this.countryId = EzString.stringWithTextOrEmpty(countryId);

        this.timeZoneId = timeZoneId;

        this.stdUTCOffset = EzString.stringWithTextOrEmpty(stdUTCOffset);

        this.dstUTCOffset = EzString.stringWithTextOrDefault(
            dstUTCOffset,
            stdUTCOffset);

        this.abbreviation = EzString.stringWithTextOrDefault(
            abbreviation,
            timeZoneId);

        this.description = EzString.stringWithTextOrDefault(
            description,
            timeZoneId);

        this.primaryCityTimeZone = EzString.stringWithTextOrDefault(
            primaryCityTimeZone,
            timeZoneId);
    }

    /**
     * @private @field
     * Stores the value for countryId
     * @type {string}
     */
    #countryId = 'US';
    /**
     * @public @property @getter
     * Gets the stored value for countryId
     * @returns {string}
     */
    get countryId() {
        return this.#countryId;
    }
    /**
     * @public @property @setter
     * Sets the stored value for countryId
     * @param {string} countryId
     */
    set countryId(countryId) {
        if (!EzString.hasLength(countryId)) {
            throw new EzBadParamException(
                'countryId',
                this,
                this.countryId);
        }

        this.#countryId = countryId;
    }

    /**
     * @private @field
     * Stores the value for timeZoneId
     * @type {string}
     */
    #timeZoneId = 'UTC';
    /**
     * @public @property @getter
     * Gets the stored value for timeZoneId
     * @returns {string}
     */
    get timeZoneId() {
        return this.#timeZoneId;
    }
    /**
     * @public @property @setter
     * Sets the stored value for timeZoneId
     * @param {string} timeZoneId
     */
    set timeZoneId(timeZoneId) {
        if (!EzString.hasText(timeZoneId)) {
            throw new EzBadParamException(
                'timeZoneId',
                this,
                ` get ${this.timeZoneId.toString()}`);
        }

        this.#timeZoneId = timeZoneId;
    }
    /**
     * @public @property @getter
     * Gets the stored value for timeZoneId
     * Alias for: timeZoneId property}
     * @returns {string}
     */
    get ID() {
        return this.#timeZoneId;
    }
    /**
     * @public @property @setter
     * Sets the stored value for timeZoneId
     * Alias for: timeZoneId property}
     * @param {string} timeZoneId
     */
    set ID(timeZoneId) {
        if (!EzString.hasText(timeZoneId)) {
            throw new EzBadParamException(
                'timeZoneId',
                this,
                ` get ${this.timeZoneId.toString()}`);
        }

        this.#timeZoneId = timeZoneId;
    }

    /**
     * @private @field
     * Stores the value for stdUTCOffset
     * @type {string}
     */
    #stdUTCOffset = '00:00';
    /**
     * @public @property @getter
     * Gets the stored value for stdUTCOffset
     * @returns {string}
     */
    get stdUTCOffset() {
        return this.#stdUTCOffset;
    }
    /**
     * @public @property @setter
     * Sets the stored value for stdUTCOffset
     * @param {string} stdUTCOffset
     */
    set stdUTCOffset(stdUTCOffset) {
        if (!EzString.hasLength(stdUTCOffset)) {
            throw new EzBadParamException(
                'stdUTCOffset',
                this,
                this.stdUTCOffset);
        }

        this.#stdUTCOffset = stdUTCOffset;
    }

    /**
     * @private @field
     * Stores the value for dstUTCOffset
     * @type {string}
     */
    #dstUTCOffset = '00:00';
    /**
     * @public @property @getter
     * Gets the stored value for dstUTCOffset
     * @returns {string}
     */
    get dstUTCOffset() {
        return this.#dstUTCOffset;
    }
    /**
     * @public @property @setter
     * Sets the stored value for dstUTCOffset
     * @param {string} dstUTCOffset
     */
    set dstUTCOffset(dstUTCOffset) {
        if (!EzString.hasLength(dstUTCOffset)) {
            throw new EzBadParamException(
                'dstUTCOffset',
                this,
                this.dstUTCOffset);
        }

        this.#dstUTCOffset = dstUTCOffset;
    }

    /**
     * @private @field
     * Stores the value for abbreviation
     * @type {string}
     */
    #abbreviation = 'UTC';
    /**
     * @public @property @getter
     * Gets the stored value for abbreviation
     * @returns {string}
     */
    get abbreviation() {
        return this.#abbreviation;
    }
    /**
     * @public @property @setter
     * Sets the stored value for abbreviation
     * @param {string} abbreviation
     */
    set abbreviation(abbreviation) {
        if (!EzString.hasLength(abbreviation)) {
            throw new EzBadParamException(
                'abbreviation',
                this,
                this.abbreviation);
        }

        this.#abbreviation = abbreviation;
    }

    /**
     * @private @field
     * Stores the value for description
     * @type {string}
     */
    #description = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the stored value for description
     * @returns {string}
     */
    get description() {
        return this.#description;
    }
    /**
     * @public @property @setter
     * Sets the stored value for description
     * @param {string} description
     */
    set description(description) {
        this.#description = EzString.stringOrEmpty(description);
    }

    /**
     * @private @field
     * Stores the primary city time zone id associated with the timeZoneId value (example: CST = 'America/Chicago')
     * @type {string}
     */
    #primaryCityTimeZone = EzString.EMPTY;
    get primaryCityTimeZone() {
        if (!EzString.hasText(this.#primaryCityTimeZone)) {
            this.#primaryCityTimeZone = this.timeZoneId;
        }

        return this.#primaryCityTimeZone;
    }
    set primaryCityTimeZone(primaryCityTimeZone) {
        this.#primaryCityTimeZone = EzString.stringWithTextOrDefault(
            primaryCityTimeZone,
            this.timeZoneId);
    }
}

/**
 * @class
 * @extends {EzEnumeration2}
 * @description
 * EzTimeZone enumeration class
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-enumeration classes with:
 *      import {
 *          // ... other enumeration classes ...
 *          EzTimeZone
 *      } from '/ezlibrary/enums/EzEnumerations.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other enumeration classes or classes that cannot
 * import EzEnumerations:
 *      import { EzTimeZone } from '/ezlibrary/date-time/EzTimeZone.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Access static methods and properties only: EzTimeZone.{method or property}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzTimeZone extends EzEnumeration2 {
    /**
     * @private @field
     * Stores the singleton instance of this enumeration class.
     * @type {EzTimeZone}
     */
    static #ezEnumerationSingleton = null;

    /**
     * @static
     * Static Initialization
     */
    static {
        if (null == this.#ezEnumerationSingleton) {
            this.#ezEnumerationSingleton = new EzTimeZone(
                // Enum property names
                [
                    'UNKNOWN',
                    'UTC',
                    'America/Adak',
                    'America/Anchorage',
                    'America/Atka',
                    'America/Boise',
                    'America/Chicago',
                    'America/Denver',
                    'America/Detroit',
                    'America/Fort_Wayne',
                    'America/Indiana/Indianapolis',
                    'America/Indiana/Knox',
                    'America/Indiana/Marengo',
                    'America/Indiana/Petersburg',
                    'America/Indiana/Tell_City',
                    'America/Indiana/Vevay',
                    'America/Indiana/Vincennes',
                    'America/Indiana/Winamac',
                    'America/Indianapolis',
                    'America/Juneau',
                    'America/Kentucky/Louisville',
                    'America/Kentucky/Monticello',
                    'America/Knox_IN',
                    'America/Los_Angeles',
                    'America/Louisville',
                    'America/Menominee',
                    'America/Metlakatla',
                    'America/New_York',
                    'America/Nome',
                    'America/North_Dakota/Beulah',
                    'America/North_Dakota/Center',
                    'America/North_Dakota/New_Salem',
                    'America/Phoenix',
                    'America/Shiprock',
                    'America/Sitka',
                    'America/Yakutat',
                    'Navajo',
                    'Pacific/Honolulu',
                    'Pacific/Johnston',
                    'US/Alaska',
                    'US/Aleutian',
                    'US/Arizona',
                    'US/Central',
                    'US/East',
                    'US/Eastern',
                    'US/Hawaii',
                    'US/Indiana',
                    'US/Michigan',
                    'US/Mountain',
                    'US/Pacific'
                ],
                // Enum property values for each enum property name as an array
                // Enter null if the values are equal to the names
                null,
                // Optional array of additional data for eac enumeration property.
                // Enter null if not needed.
                [
                    new EzTimeZoneInfo('US', 'UNKNOWN', '00:00', '00:00', 'UTC', 'Unknown time zone id (assumes UTC)'),
                    new EzTimeZoneInfo('US', 'UTC', '00:00', '00:00', 'UTC'),
                    new EzTimeZoneInfo('US', 'America/Adak', '−10:00', '−09:00'),
                    new EzTimeZoneInfo('US', 'America/Anchorage', '−09:00', '−08:00'),
                    new EzTimeZoneInfo('US', 'America/Atka', '−10:00', '−09:00'),
                    new EzTimeZoneInfo('US', 'America/Boise', '−07:00', '−06:00'),
                    new EzTimeZoneInfo('US', 'America/Chicago', '−06:00', '−05:00'),
                    new EzTimeZoneInfo('US', 'America/Denver', '−07:00', '−06:00'),
                    new EzTimeZoneInfo('US', 'America/Detroit', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'America/Fort_Wayne', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'America/Indiana/Indianapolis', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'America/Indiana/Knox', '−06:00', '−05:00'),
                    new EzTimeZoneInfo('US', 'America/Indiana/Marengo', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'America/Indiana/Petersburg', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'America/Indiana/Tell_City', '−06:00', '−05:00'),
                    new EzTimeZoneInfo('US', 'America/Indiana/Vevay', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'America/Indiana/Vincennes', '−05:00', ''),
                    new EzTimeZoneInfo('US', 'America/Indiana/Winamac', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'America/Indianapolis', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'America/Juneau', '−09:00', '−08:00'),
                    new EzTimeZoneInfo('US', 'America/Kentucky/Louisville', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'America/Kentucky/Monticello', '-05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'America/Knox_IN', '−06:00', '−05:00'),
                    new EzTimeZoneInfo('US', 'America/Los_Angeles', '−08:00', '−07:00'),
                    new EzTimeZoneInfo('US', 'America/Louisville', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'America/Menominee', '−06:00', '−05:00'),
                    new EzTimeZoneInfo('US', 'America/Metlakatla', '−09:00', '−08:00'),
                    new EzTimeZoneInfo('US', 'America/New_York', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'America/Nome', '−09:00', '    −08:00'),
                    new EzTimeZoneInfo('US', 'America/North_Dakota/Beulah', '−06:00', '−05:00'),
                    new EzTimeZoneInfo('US', 'America/North_Dakota/Center', '−06:00', '−05:00'),
                    new EzTimeZoneInfo('US', 'America/North_Dakota/New_Salem', '−06:00', '−05:00'),
                    new EzTimeZoneInfo('US', 'America/Phoenix', '−07:00', '−07:00'),
                    new EzTimeZoneInfo('US', 'America/Shiprock', '−07:00', '−06:00'),
                    new EzTimeZoneInfo('US', 'America/Sitka', '−09:00', '−08:00'),
                    new EzTimeZoneInfo('US', 'America/Yakutat', '−09:00', '−08:00'),
                    new EzTimeZoneInfo('US', 'Navajo', '−07:00', '−06:00'),
                    new EzTimeZoneInfo('US', 'Pacific/Honolulu', '−10:00', '−10:00'),
                    new EzTimeZoneInfo('US', 'Pacific/Johnston', '−10:00', '−10:00'),
                    new EzTimeZoneInfo('US', 'US/Alaska', '−09:00', '−08:00'),
                    new EzTimeZoneInfo('US', 'US/Aleutian', '−10:00', '−09:00'),
                    new EzTimeZoneInfo('US', 'US/Arizona', '−07:00', '−07:00'),
                    new EzTimeZoneInfo('US', 'US/Central', '−06:00', '−05:00'),
                    new EzTimeZoneInfo('US', 'US/East', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'US/Eastern', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'US/Hawaii', '−10:00', '−10:00'),
                    new EzTimeZoneInfo('US', 'US/Indiana', '−06:00', '−05:00'),
                    new EzTimeZoneInfo('US', 'US/Michigan', '−05:00', '−04:00'),
                    new EzTimeZoneInfo('US', 'US/Mountain', '−07:00', '−06:00'),
                    new EzTimeZoneInfo('US', 'US/Pacific', '−08:00', '−07:00')
                ]);
        }
    }
}
